import { useCallback, useEffect, useState } from "react";
import { listenFollowStatus } from "../lib/follows";

type FollowState = {
  key: string;
  following: boolean;
  loading: boolean;
  error: unknown | null;
};

export function useFollowStatus(
  followerAccountId: string | null | undefined,
  targetAccountId: string | null | undefined
) {
  const follower = String(followerAccountId ?? "").trim();
  const target = String(targetAccountId ?? "").trim();
  const key = `${follower}\u0000${target}`;
  const [state, setState] = useState<FollowState>({
    key: "",
    following: false,
    loading: true,
    error: null,
  });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!follower || !target || follower === target) {
      setState({ key, following: false, loading: false, error: null });
      return;
    }

    let active = true;
    setState({ key, following: false, loading: true, error: null });
    const unsubscribe = listenFollowStatus(
      follower,
      target,
      (following) => {
        if (active) setState({ key, following, loading: false, error: null });
      },
      (error) => {
        if (active) setState({ key, following: false, loading: false, error });
      }
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [follower, key, retryKey, target]);

  const retry = useCallback(() => setRetryKey((value) => value + 1), []);
  const current = state.key === key
    ? state
    : { key, following: false, loading: !!follower && !!target && follower !== target, error: null };

  return { ...current, retry };
}
